import React, { useContext, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { useCollectionsLocation } from '../../shared/useCollectionsLocation';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { departmentGroupMembershipsUrl, useDepartmentGroupMemberships } from '../hooks/useDepartmentGroupMemberships';
import { useAssignableUsers } from '../hooks/useAssignableUsers';
import {
  addGroupMembershipVariables,
  removeGroupMembershipVariables,
  useAddGroupMembership,
  useRemoveGroupMembership,
} from '../hooks/useGroupMembershipMutations';
import { AssignableUser, DepartmentGroupWithMembers } from '../model';

const assignableUserLabel = (user: AssignableUser): string => `${user.full_name} (${user.email})`;

const departmentGroupDisplayName = (groupId: string): string =>
  groupId === 'ALL_DEPARTMENTS' ? 'All Departments' : groupId;

const filterAssignableUsers = (options: AssignableUser[], inputValue: string): AssignableUser[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((user) => assignableUserLabel(user).toLowerCase().includes(query));
};

const sectionTitleSx = {
  color: 'primary.main',
  fontWeight: 600,
  fontSize: '14px',
} as const;

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'visible',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    borderColor: 'primary.light',
    bgcolor: 'grey.50',
  },
} as const;

const DepartmentGroupConfigPage: React.FC = () => {
  const location = useCollectionsLocation();
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const { data: groupsResponse, isLoading } = useDepartmentGroupMemberships(location ?? '', !!location);
  const { data: usersResponse } = useAssignableUsers();
  const addMembership = useAddGroupMembership();
  const removeMembership = useRemoveGroupMembership();

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pickerKeys, setPickerKeys] = useState<Record<string, number>>({});

  const bumpPicker = (groupId: string) => {
    setPickerKeys((prev) => ({ ...prev, [groupId]: (prev[groupId] ?? 0) + 1 }));
  };

  const groups: DepartmentGroupWithMembers[] = groupsResponse?.data ?? [];
  const assignableUsers: AssignableUser[] = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Departments" titleIcon={<GroupsIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [departmentGroupMembershipsUrl(), { location }] });
  };

  const handleAdd = async (groupId: string, user: AssignableUser | null) => {
    if (!user) return;
    setBusyKey(`${groupId}:${user.id}`);
    try {
      await addMembership.mutateAsync(addGroupMembershipVariables(user.id, groupId, location));
      await refresh();
      bumpPicker(groupId);
      showSuccessSnackBar?.(`Added ${user.full_name} to ${departmentGroupDisplayName(groupId)}`);
    } catch (e) {
      showErrorSnackBar?.('Could not add user to department');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (membershipId: number, groupId: string, fullName: string) => {
    setBusyKey(`${groupId}:${membershipId}`);
    try {
      await removeMembership.mutateAsync(removeGroupMembershipVariables(membershipId, location));
      await refresh();
      showSuccessSnackBar?.(`Removed ${fullName} from ${departmentGroupDisplayName(groupId)}`);
    } catch (e) {
      showErrorSnackBar?.('Could not remove user from department');
    } finally {
      setBusyKey(null);
    }
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Departments" titleIcon={<GroupsIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Departments" titleIcon={<GroupsIcon />} />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" sx={sectionTitleSx} gutterBottom>
          Departments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Expand a department to add or remove the users who belong to it.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: 'primary.light', opacity: 0.5 }} />

        <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
          {groups.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No departments for this location.
            </Typography>
          )}
          <Grid container columnSpacing={1.5} rowSpacing={0.5}>
            {groups.map((group) => {
              const memberIds = new Set(group.users.map((u) => u.id));
              const options = assignableUsers.filter((u) => !memberIds.has(u.id));
              return (
                <Grid item xs={12} md={6} key={group.group_id}>
                  <Accordion disableGutters sx={accordionSx}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                      sx={{
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, mr: 0.5 }}>
                        {departmentGroupDisplayName(group.group_id)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 'auto', mr: 1, whiteSpace: 'nowrap' }}
                      >
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {group.users.length}
                        </Box>{' '}
                        users
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ overflow: 'visible', pt: 0 }}>
                      <List dense disablePadding>
                        {group.users.map((user) => (
                          <ListItem
                            key={user.membership_id}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              bgcolor: 'grey.50',
                              borderLeft: '2px solid',
                              borderLeftColor: 'primary.light',
                            }}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                size="small"
                                disabled={busyKey === `${group.group_id}:${user.membership_id}`}
                                onClick={() => handleRemove(user.membership_id, group.group_id, user.full_name)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={user.full_name}
                              secondary={user.email}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Autocomplete
                        key={`add-user-${group.group_id}-${pickerKeys[group.group_id] ?? 0}`}
                        options={options}
                        getOptionLabel={assignableUserLabel}
                        isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
                        value={null}
                        blurOnSelect
                        filterOptions={(opts, state) => filterAssignableUsers(opts, state.inputValue)}
                        onChange={(_event, value) => handleAdd(group.group_id, value)}
                        ListboxProps={{ style: { maxHeight: 280 } }}
                        componentsProps={{ popper: { placement: 'bottom-start', style: { zIndex: 1500 } } }}
                        renderInput={(params) => (
                          <TextField {...params} label="Add user" size="small" color="primary" autoComplete="off" />
                        )}
                        sx={{ maxWidth: 420, mt: 1 }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DepartmentGroupConfigPage;
